import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { SectionHeader, Field, Button, Loading, Avatar, Chip } from "@/components/brand";
import { formatR } from "@/lib/format";
import { cn } from "@/lib/utils";

const DAYS = [["1", "Mon"], ["2", "Tue"], ["3", "Wed"], ["4", "Thu"], ["5", "Fri"], ["6", "Sat"], ["0", "Sun"]];

export default function TrainerProfileEdit() {
  const qc = useQueryClient();
  const cats = useQuery({ queryKey: ["categories"], queryFn: () => api("/categories") });
  const me = useQuery({ queryKey: ["trainer", "me"], queryFn: () => api("/trainer/me") });
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (me.data && !form) setForm({
      headline: me.data.headline || "", bio: me.data.bio || "", location: me.data.location || "",
      experience_years: me.data.experience_years || 1, avatar: me.data.avatar || "",
      specialties: me.data.specialties || [], private_pricing: me.data.private_pricing || [],
      availability: me.data.availability || [],
    });
  }, [me.data, form]);

  const save = useMutation({
    mutationFn: () => api("/trainer/me", { method: "PUT", body: {
      ...form, experience_years: Number(form.experience_years),
      private_pricing: form.private_pricing.map((p) => ({ duration_min: Number(p.duration_min), price: Number(p.price) })),
    } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["trainer", "me"] }); toast.success("Profile saved"); },
    onError: (e) => toast.error(e.message),
  });

  if (me.isLoading || !form) return <Loading />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSpec = (k) => set("specialties", form.specialties.includes(k) ? form.specialties.filter((s) => s !== k) : [...form.specialties, k]);
  const dayActive = (d) => form.availability.some((a) => String(a.day) === d);
  const toggleDay = (d) => set("availability", dayActive(d) ? form.availability.filter((a) => String(a.day) !== d) : [...form.availability, { day: Number(d), start: "08:00", end: "17:00" }]);

  return (
    <div data-testid="trainer-profile-screen" className="max-w-2xl space-y-5">
      <SectionHeader title="Trainer Profile" subtitle="How members see you across Sofit." />
      <div className="flex items-center gap-4">
        <Avatar uri={form.avatar} size={72} name={me.data.name} />
        <Field label="Avatar image URL" testID="profile-avatar" value={form.avatar} onChange={(e) => set("avatar", e.target.value)} className="flex-1" />
      </div>
      <Field label="Headline" testID="profile-headline" value={form.headline} onChange={(e) => set("headline", e.target.value)} placeholder="HIIT · Strength · Weight Loss" />
      <Field label="Bio" as="textarea" testID="profile-bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Location" testID="profile-location" value={form.location} onChange={(e) => set("location", e.target.value)} />
        <Field label="Years experience" testID="profile-experience" type="number" value={form.experience_years} onChange={(e) => set("experience_years", e.target.value)} />
      </div>

      <div>
        <div className="font-body font-semibold text-[13px] text-ink mb-2">Specialties</div>
        <div className="flex gap-2 flex-wrap">
          {(cats.data ?? []).map((c) => <Chip key={c.key} label={c.name} selected={form.specialties.includes(c.key)} onClick={() => toggleSpec(c.key)} testID={`spec-${c.key}`} />)}
        </div>
      </div>

      <div>
        <div className="font-body font-semibold text-[13px] text-ink mb-2">Private session pricing</div>
        <div className="space-y-2">
          {form.private_pricing.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="number" data-testid={`pricing-min-${i}`} value={p.duration_min} onChange={(e) => set("private_pricing", form.private_pricing.map((x, j) => j === i ? { ...x, duration_min: e.target.value } : x))} className="w-24 h-11 px-3 rounded-md border border-line bg-surface2 font-body text-sm" placeholder="min" />
              <span className="font-body text-sm text-mutedgrey">min for R</span>
              <input type="number" data-testid={`pricing-price-${i}`} value={p.price} onChange={(e) => set("private_pricing", form.private_pricing.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} className="w-28 h-11 px-3 rounded-md border border-line bg-surface2 font-body text-sm" placeholder="price" />
              <button onClick={() => set("private_pricing", form.private_pricing.filter((_, j) => j !== i))} className="text-mutedgrey hover:text-destructive"><Trash2 size={16} /></button>
            </div>
          ))}
          <button data-testid="add-pricing" onClick={() => set("private_pricing", [...form.private_pricing, { duration_min: 60, price: 350 }])} className="inline-flex items-center gap-1 font-body font-semibold text-sm text-brand"><Plus size={16} />Add duration</button>
        </div>
      </div>

      <div>
        <div className="font-body font-semibold text-[13px] text-ink mb-2">Weekly availability (08:00–17:00)</div>
        <div className="flex gap-1.5 flex-wrap">
          {DAYS.map(([d, l]) => <button key={d} data-testid={`avail-${l}`} onClick={() => toggleDay(d)} className={cn("w-11 h-9 rounded-md border font-body font-semibold text-xs", dayActive(d) ? "bg-brand border-brand text-white" : "border-line text-ink")}>{l}</button>)}
        </div>
      </div>

      <Button testID="profile-save" title="Save Profile" loading={save.isPending} onClick={() => save.mutate()} full />
    </div>
  );
}
